import { Fragment, useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CONSUMIDOR_FINAL, formatearCOP, formatearFecha } from '@tanteo/compartido';
import { anularVentaRpc, obtenerVenta, type VentaDetalle } from '../lib/api';
import { useApp } from '../lib/contexto';
import {
  AreaTexto,
  Boton,
  CeldaPlata,
  EncabezadoTabla,
  Esqueleto,
  FilaAcciones,
  Insignia,
  MensajeError,
  TablaAncha,
  Tarjeta,
  TextoAyuda,
} from '../ui/base';
import { Modal } from '../ui/Modal';

/*
  La aclaración obligatoria niega la factura, así que la palabra es intencional
  (docs/08-legal-normativo.md §2). Una sola vez, y de aquí la toman el impreso y el correo.
*/
// eslint-disable-next-line no-restricted-syntax
const NOTA_LEGAL = 'Comprobante interno de venta — no es factura de venta ni documento equivalente.';

export function DetalleComprobante() {
  const { negocio } = useApp();
  const { id } = useParams<{ id: string }>();
  const [venta, setVenta] = useState<VentaDetalle | null>(null);
  const [error, setError] = useState('');
  const [anulando, setAnulando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const cargar = useCallback(() => {
    if (!negocio || !id) return;
    obtenerVenta(negocio.negocioId, id)
      .then(setVenta)
      .catch(() => setError('No encontramos este comprobante.'));
  }, [negocio, id]);

  useEffect(cargar, [cargar]);

  async function anular() {
    if (!id || motivo.trim().length < 3) return;
    setOcupado(true);
    try {
      await anularVentaRpc(id, motivo.trim());
      setAnulando(false);
      setMotivo('');
      cargar();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo anular.');
    } finally {
      setOcupado(false);
    }
  }

  // El marcador lo usa el e2e de «recurso de otro negocio → 404»: RLS devuelve 0 filas y la
  // pantalla tiene que decirlo, no quedarse en un esqueleto para siempre.
  if (error) return <MensajeError e2e="comprobante-no-encontrado">{error}</MensajeError>;
  if (!venta || !negocio) return <Esqueleto lineas={6} />;

  const esConsumidorFinal = !venta.cliente || venta.cliente.numeroDocumento === CONSUMIDOR_FINAL.numeroDocumento;
  const correoCliente = esConsumidorFinal ? null : venta.cliente!.correo;

  /**
   * El envío lo hace el negocio desde su propio correo: se abre su cliente con el mensaje redactado.
   * Un `mailto:` se recorta alrededor de los 2000 caracteres: con muchas líneas va el conteo.
   */
  function abrirCorreo() {
    if (!correoCliente || !venta || !negocio) return;
    const asunto = `Comprobante ${venta.consecutivo} · ${negocio.negocioNombre}`;
    const detalle = venta.lineas.map((i) => `${i.cantidad} × ${i.nombre}: ${formatearCOP(i.totalLinea)}`).join('\n');
    const resumen =
      detalle.length > 1200 ? `${venta.lineas.length} productos (el detalle está en el comprobante impreso)` : detalle;
    const cuerpo = [
      `${negocio.negocioNombre}`,
      `${venta.consecutivo} · ${formatearFecha(venta.fecha)} ${venta.hora}`,
      '',
      resumen,
      '',
      ...(venta.descuento > 0 ? [`Descuento: −${formatearCOP(venta.descuento)}`] : []),
      `TOTAL: ${formatearCOP(venta.total)}`,
      ...(venta.pagos.length > 1
        ? venta.pagos.map((g) => `${g.cuenta}: ${formatearCOP(g.monto)}`)
        : [`Método de pago: ${venta.cuenta}`]),
      '',
      NOTA_LEGAL,
    ].join('\n');
    window.location.href = `mailto:${encodeURIComponent(correoCliente)}?subject=${encodeURIComponent(
      asunto
    )}&body=${encodeURIComponent(cuerpo)}`;
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div className="no-imprimir flex flex-wrap items-center justify-between gap-3">
        <Link to="/comprobantes" className="text-info underline">
          ← Comprobantes
        </Link>
        <div className="flex gap-2">
          <Boton variante="contorno" onClick={() => window.print()}>
            Imprimir
          </Boton>
          {correoCliente && (
            <Boton variante="contorno" onClick={abrirCorreo} data-e2e="enviar-comprobante-correo">
              Enviar por correo
            </Boton>
          )}
          {negocio.rol === 'propietario' && venta.estado === 'emitida' && (
            <Boton variante="peligro" onClick={() => setAnulando(true)} data-e2e="abrir-anular">
              Anular
            </Boton>
          )}
        </div>
      </div>

      <Tarjeta className="comprobante-imprimible">
        <div className="text-center">
          <h1 className="text-titulo font-extrabold">{negocio.negocioNombre}</h1>
          <p className="num text-titulo font-bold" data-e2e="detalle-consecutivo">
            {venta.consecutivo}
          </p>
          <p className="text-muted text-contenido">
            {formatearFecha(venta.fecha)} · {venta.hora}
          </p>
          {venta.pedidoNumero && (
            <p className="text-contenido font-semibold" data-e2e="comprobante-pedido">
              Pedido {venta.pedidoNumero}
            </p>
          )}
          {venta.estado === 'anulada' && (
            <p className="mt-1">
              <Insignia tono="peligro">ANULADO — {venta.motivoAnulacion}</Insignia>
            </p>
          )}
        </div>
        {/* Quién y a quién, antes de qué se vendió. */}
        <div className="mt-3 space-y-0.5 text-contenido">
          <p className="text-muted flex justify-between">
            <span>Atendió</span>
            <span>{venta.cajero}</span>
          </p>
          <p className="text-muted flex justify-between">
            <span>Cliente</span>
            <span>{esConsumidorFinal ? 'Consumidor final' : `${venta.cliente!.nombre} (${venta.cliente!.numeroDocumento})`}</span>
          </p>
          {correoCliente && (
            <p className="text-muted flex justify-between gap-2">
              <span>Correo</span>
              <span className="truncate">{correoCliente}</span>
            </p>
          )}
        </div>
        <hr className="border-border my-3" />
        {/* Encabezados cortos: en el impreso esta tabla vive en 58 mm a 11 px (`styles/impresion.css`). */}
        <TablaAncha className="text-contenido">
          <EncabezadoTabla>
            <th className="py-1 pr-2 font-bold">Cant.</th>
            <th className="py-1 pr-2 font-bold">Descripción</th>
            <th className="py-1 text-right font-bold">Subtotal</th>
          </EncabezadoTabla>
          <tbody>
            {venta.lineas.map((i) => (
              <Fragment key={i.linea}>
                <tr>
                  <td className="num py-0.5 pr-2 align-top">{i.cantidad}×</td>
                  <td className="py-0.5 pr-2">{i.nombre}</td>
                  <CeldaPlata className="py-0.5 pr-0">{formatearCOP(i.totalLinea)}</CeldaPlata>
                </tr>
                {/* La rebaja del producto consta en el comprobante: es lo que la saca de la base del IVA. */}
                {i.rebaja && (
                  <tr data-e2e="comprobante-rebaja">
                    <td />
                    <td className="py-0.5 pr-2 pl-2 text-etiqueta">
                      {i.rebaja.nombre ?? 'Descuento'}
                      {i.rebaja.tipo === 'pct' && i.rebaja.valor !== null ? ` −${i.rebaja.valor}%` : ''}
                    </td>
                    <CeldaPlata className="py-0.5 pr-0 text-etiqueta">−{formatearCOP(i.rebaja.monto)}</CeldaPlata>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </TablaAncha>
        <hr className="border-border my-3" />
        <div className="space-y-0.5 text-contenido">
          {venta.descuento > 0 ? (
            <>
              <p className="flex justify-between">
                <span>Subtotal</span>
                <span className="num">{formatearCOP(venta.subtotal + venta.descuento)}</span>
              </p>
              {venta.descuentoLineas > 0 && (
                <p className="flex justify-between">
                  <span>Desc. productos</span>
                  <span className="num">−{formatearCOP(venta.descuentoLineas)}</span>
                </p>
              )}
              {venta.descuento > venta.descuentoLineas && (
                <p className="flex justify-between" data-e2e="comprobante-desc-venta">
                  <span>
                    Desc. venta
                    {venta.descuentoVenta?.tipo === 'pct' ? ` ${venta.descuentoVenta.valor}%` : ''}
                    {venta.descuentoVenta?.nombre ? ` (${venta.descuentoVenta.nombre})` : ''}
                  </span>
                  <span className="num">−{formatearCOP(venta.descuento - venta.descuentoLineas)}</span>
                </p>
              )}
              <p className="flex justify-between">
                <span>Base</span>
                <span className="num">{formatearCOP(venta.subtotal)}</span>
              </p>
            </>
          ) : (
            <p className="flex justify-between">
              <span>Subtotal</span>
              <span className="num">{formatearCOP(venta.subtotal)}</span>
            </p>
          )}
          <p className="flex justify-between">
            <span>Impuesto</span>
            <span className="num">{formatearCOP(venta.impuesto)}</span>
          </p>
          <p className="flex justify-between text-titulo font-extrabold">
            <span>TOTAL</span>
            <span className="num" data-e2e="comprobante-total">{formatearCOP(venta.total)}</span>
          </p>
          {/* Una línea por pago: con uno se lee igual que siempre, con varios se ve el reparto. */}
          {(venta.pagos.length > 1 ? venta.pagos : [{ cuenta: venta.cuenta, monto: venta.total }]).map((g, i) => (
            <p className="text-muted flex justify-between" key={`${g.cuenta}-${i}`} data-e2e="comprobante-pago">
              <span>{venta.pagos.length > 1 ? g.cuenta : 'Método de pago'}</span>
              <span className={venta.pagos.length > 1 ? 'num' : undefined}>
                {venta.pagos.length > 1 ? formatearCOP(g.monto) : venta.cuenta}
              </span>
            </p>
          ))}
        </div>
        <p className="text-muted mt-4 text-center text-etiqueta">{NOTA_LEGAL}</p>
      </Tarjeta>

      <Modal abierto={anulando} titulo={`Anular ${venta.consecutivo}`} onCerrar={() => setAnulando(false)}>
        <TextoAyuda>
          El comprobante no se borra: queda marcado como anulado y deja de sumar en el cuadre. El consecutivo no se reutiliza.
        </TextoAyuda>
        <AreaTexto
          etiqueta="Motivo (obligatorio)"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          rows={2}
          data-e2e="motivo-anulacion"
        />
        <FilaAcciones className="mt-4">
          <Boton variante="contorno" onClick={() => setAnulando(false)} disabled={ocupado}>
            Volver
          </Boton>
          <Boton variante="peligro" onClick={() => void anular()} disabled={ocupado || motivo.trim().length < 3} data-e2e="confirmar-anular">
            {ocupado ? 'Anulando…' : 'Anular comprobante'}
          </Boton>
        </FilaAcciones>
      </Modal>
    </div>
  );
}
